import { For, Match, Show, Switch, createEffect, createSignal, onCleanup, onMount } from "solid-js";
import { applyMessage, canResumeEntry, correlationId, interaction, publicEntryPointId } from "./protocol";
import type { Component, Field, ParticipantAction, PendingInteraction, RuntimeMessage, SessionState } from "./types";

const buildId = import.meta.env.VITE_BUILD_ID || "development";
const inline = new URLSearchParams(location.search).get("mode") === "inline";
const entryPointId = publicEntryPointId(location.pathname);

export default function App() {
  const [state, setState] = createSignal<SessionState>();
  const [connection, setConnection] = createSignal<"checking" | "idle" | "starting" | "connecting" | "connected" | "retrying" | "completed">("checking");
  const [initializing, setInitializing] = createSignal(false);
  const [submitting, setSubmitting] = createSignal(false);
  const [problem, setProblem] = createSignal("");
  let socket: WebSocket | undefined;
  let reconnectTimer: number | undefined;
  let reconnectAttempt = 0;
  let pendingInteraction: PendingInteraction | undefined;
  const interactionStarted = new Map<string, number>();
  const reportRenderFailure = () => sendTelemetry("journey.render_failed");

  onMount(() => {
    window.addEventListener("error", reportRenderFailure);
    window.addEventListener("unhandledrejection", reportRenderFailure);
    void resumeJourney();
  });

  async function resumeJourney() {
    setConnection("checking");
    setProblem("");
    try {
      const response = await fetch("/api/session", { credentials: "same-origin" });
      if (response.status === 204) {
        setConnection("idle");
        return;
      }
      if (!response.ok) throw new Error("resume unavailable");
      const session = await response.json() as SessionState;
      if (!canResumeEntry(location.pathname, entryPointId, session.entryPointId)) {
        setConnection("idle");
        return;
      }
      setState(session);
      if (session.status === "resolved") setConnection("completed");
      else connect();
    } catch {
      setProblem("The journey is unavailable right now. Please retry in a moment.");
      setConnection("retrying");
    }
  }

  async function startJourney() {
    if (connection() === "starting") return;
    if (!entryPointId) {
      setProblem("This journey is unavailable.");
      return;
    }
    setConnection("starting");
    setInitializing(true);
    setProblem("");
    try {
      const source = new URLSearchParams(location.search).get("utm_source") || "direct";
      const response = await fetch("/api/session/start", {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ entryPointId, source: source.slice(0, 100), correlationId: correlationId() }),
      });
      if (!response.ok) throw new Error("start unavailable");
      const session = await response.json() as SessionState;
      setState(session);
      if (session.interface?.component.kind === "continue") queueInteraction(session, true);
      else setInitializing(false);
      connect();
    } catch {
      setInitializing(false);
      setProblem("The journey cannot start right now. Please retry in a moment.");
      setConnection("idle");
    }
  }

  onCleanup(() => {
    window.removeEventListener("error", reportRenderFailure);
    window.removeEventListener("unhandledrejection", reportRenderFailure);
    socket?.close();
    if (reconnectTimer) clearTimeout(reconnectTimer);
  });

  createEffect(() => {
    document.title = state()?.result ? `${state()!.result!.presentation.headline} · questionette` : "Find your next step · questionette";
  });

  function connect() {
    if (socket?.readyState === WebSocket.OPEN || socket?.readyState === WebSocket.CONNECTING) return;
    setConnection(reconnectAttempt ? "retrying" : "connecting");
    const scheme = location.protocol === "https:" ? "wss:" : "ws:";
    socket = new WebSocket(`${scheme}//${location.host}/socket`);
    socket.onopen = () => {
      reconnectAttempt = 0;
      setConnection("connected");
      setProblem("");
      socket?.send(JSON.stringify({ type: "client.ready", protocolVersion: 1, correlationId: correlationId(), buildId }));
      const current = state();
      if (current && pendingInteraction?.expectedRevision === current.revision && pendingInteraction.interfaceId === current.interface?.id) {
        setSubmitting(true);
        socket?.send(JSON.stringify(pendingInteraction));
      } else {
        pendingInteraction = undefined;
      }
    };
    socket.onmessage = (event) => receive(JSON.parse(event.data) as RuntimeMessage);
    socket.onerror = () => {
      sendTelemetry("journey.connection_failed");
      setProblem("Connection lost. Reconnecting…");
    };
    socket.onclose = () => {
      if (state()?.status === "resolved") return;
      setConnection("retrying");
      reconnectAttempt += 1;
      reconnectTimer = window.setTimeout(connect, Math.min(5000, 400 * 2 ** reconnectAttempt));
    };
  }

  function receive(message: RuntimeMessage) {
    const current = state();
    if (!current) return;
    if (["session.accepted", "interface.replace", "interaction.committed", "session.completed"].includes(message.type)) {
      const next = applyMessage(current, message);
      setState(next);
      if (message.type === "interaction.committed" || message.type === "session.completed") {
        pendingInteraction = undefined;
        setInitializing(false);
        const started = interactionStarted.get(message.correlationId);
        if (started !== undefined) sendTelemetry("journey.interaction_latency", Math.round(performance.now() - started));
        interactionStarted.delete(message.correlationId);
      }
      setSubmitting(false);
      setProblem("");
      if (message.type === "session.completed") setConnection("completed");
      return;
    }
    if (message.type === "retry.after") {
      setSubmitting(false);
      setProblem(message.message || "The answer is not yet accepted. Retry safely.");
      return;
    }
    if (message.type === "problem") {
      setSubmitting(false);
      setProblem(message.message || "The interaction was not accepted.");
    }
  }

  function submit(value: unknown) {
    const current = state();
    if (!current || !current.interface || submitting()) return;
    const message = queueInteraction(current, value);
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
    else connect();
  }

  function goBack() {
    const current = state();
    if (!current || !current.interface || !(current.canGoBack ?? current.screenIndex > 1) || submitting()) return;
    const message = queueInteraction(current, "back", "navigation_back");
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
    else connect();
  }

  function queueInteraction(current: SessionState, value: unknown, componentId?: string): PendingInteraction {
    const message = interaction(current, value, componentId);
    pendingInteraction = message;
    interactionStarted.set(message.correlationId, performance.now());
    setSubmitting(true);
    setProblem("");
    return message;
  }

  function sendTelemetry(name: "journey.render_failed" | "journey.connection_failed" | "journey.interaction_latency", durationMs?: number) {
    if (socket?.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({ type: "client.telemetry", protocolVersion: 1, correlationId: correlationId(), name, durationMs }));
  }

  function retryPending() {
    const current = state();
    const pending = current && pendingInteraction?.expectedRevision === current.revision && pendingInteraction.interfaceId === current.interface?.id ? pendingInteraction : undefined;
    if (pending && socket?.readyState === WebSocket.OPEN) {
      setSubmitting(true);
      setProblem("");
      socket.send(JSON.stringify(pending));
    } else connect();
  }

  async function resetDevSession() {
    setProblem("");
    try {
      const response = await fetch("/api/session/reset", { method: "POST", credentials: "same-origin" });
      if (!response.ok) throw new Error("reset unavailable");
      socket?.close();
      pendingInteraction = undefined;
      location.reload();
    } catch {
      setProblem("The test session could not be reset.");
    }
  }

  const connectionLabel = () => ({
    checking: "Checking for a saved journey",
    idle: "Journey not started",
    starting: "Initializing journey",
    connecting: "Connecting securely",
    connected: "Answers save as you go",
    retrying: "Reconnecting",
    completed: "Journey complete",
  })[connection()];

  return (
    <div class="app-shell" classList={{ inline }}>
      <Show when={!inline}><header class="site-header">
        <a href="https://questionette.com" class="brand" aria-label="questionette home">questionette</a>
        <span class={`connection ${connection()}`}><i />{connectionLabel()}</span>
      </header></Show>
      <main>
        <Switch>
          <Match when={!state() && connection() === "idle"}>
            <Landing starting={false} problem={problem()} onStart={startJourney} />
          </Match>
          <Match when={initializing() || !state()}>
            <Loading problem={problem()} label={initializing() ? "Starting…" : undefined} onRetry={connection() === "idle" ? startJourney : resumeJourney} />
          </Match>
          <Match when={state()}>
            {(session) => (
              <Switch>
                <Match when={session().result}>
                  {(result) => <ResultView result={result()} />}
                </Match>
                <Match when={session().interface}>
                  {(screen) => (
                    <JourneyScreen
                      screen={screen()}
                      step={Math.max(1, Math.min(session().screenIndex, session().screenCount || 6))}
                      stepCount={session().screenCount || 6}
                      answers={session().answers || {}}
                      canGoBack={session().canGoBack ?? (session().screenIndex > 1 && !screen().id.startsWith("clarification_"))}
                      submitting={submitting()}
                      problem={problem()}
                      onBack={goBack}
                      onSubmit={submit}
                      onRetry={retryPending}
                    />
                  )}
                </Match>
                <Match when={session().status === "interpreting"}>
                  <Loading problem={problem()} label="Checking your answers…" onRetry={retryPending} />
                </Match>
              </Switch>
            )}
          </Match>
        </Switch>
      </main>
      <Show when={!inline}><footer><span>questionette</span><span>A clear route for every inbound conversation.</span></footer></Show>
      <button class="dev-reset" type="button" onClick={resetDevSession}>Reset journey</button>
    </div>
  );
}

function Landing(props: { starting: boolean; problem: string; onStart: () => void }) {
  return <section class="journey landing" aria-labelledby="landing-title">
    <p class="eyebrow">Your next step</p>
    <h1 id="landing-title">Tell us how leads reach your team.</h1>
    <p class="lede">Share a few details about your sales process. You’ll get a clear next step. Self-service stays open to everyone who can use questionette, while strong-fit leads can also receive guided help.</p>
    <ol class="lifecycle" aria-label="What happens next"><li><strong>1</strong><span>Describe your current process</span></li><li><strong>2</strong><span>See how questionette routes it</span></li><li><strong>3</strong><span>Choose your next step</span></li></ol>
    <button class="primary wide" disabled={props.starting} onClick={props.onStart}>Start the journey<Arrow /></button>
    <Show when={props.problem}><p class="error">{props.problem}</p></Show>
  </section>;
}

function JourneyScreen(props: { screen: NonNullable<SessionState["interface"]>; step: number; stepCount: number; answers: Record<string, unknown>; canGoBack: boolean; submitting: boolean; problem: string; onBack: () => void; onSubmit: (value: unknown) => void; onRetry: () => void }) {
  return (
    <section class="journey" aria-labelledby="screen-title">
      <div class="journey-nav"><Show when={props.canGoBack}><button class="back-button" disabled={props.submitting} onClick={props.onBack}><BackArrow />Back</button></Show><span>{props.step} of {props.stepCount}</span></div>
      <div class="progress" role="progressbar" aria-label="Journey progress" aria-valuemin="1" aria-valuemax={props.stepCount} aria-valuenow={props.step}><span style={{ width: `${Math.round(props.step / props.stepCount * 10000) / 100}%` }} /></div>
      <p class="eyebrow">{props.screen.eyebrow}</p>
      <h1 id="screen-title">{props.screen.title}</h1>
      <p class="lede">{props.screen.body}</p>
      <Show keyed when={props.screen.component}>
        {(component) => <ComponentView component={component} answers={props.answers} submitting={props.submitting} onSubmit={props.onSubmit} />}
      </Show>
      <div class="status" aria-live="polite">
        <Show when={props.submitting}><span class="saving">Saving your answer…</span></Show>
        <Show when={props.problem}><span class="error">{props.problem} <button class="text-button" onClick={props.onRetry}>Retry</button></span></Show>
      </div>
    </section>
  );
}

function ComponentView(props: { component: Component; answers: Record<string, unknown>; submitting: boolean; onSubmit: (value: unknown) => void }) {
  const component = props.component;
  if (component.kind === "continue") return <button class="primary wide" disabled={props.submitting} onClick={() => props.onSubmit(true)}>{component.label || "Continue"}<Arrow /></button>;
  if (component.kind === "cards" || component.kind === "choice") return <div class="cards"><For each={component.options}>{(option, index) => <button class="card" classList={{ selected: props.answers[component.factorId || component.id] === option.value }} disabled={props.submitting} onClick={() => props.onSubmit(option.value)}><span class="card-index">{String(index() + 1).padStart(2, "0")}</span><span><strong>{option.label}</strong><Show when={option.detail}><small>{option.detail}</small></Show></span><Arrow /></button>}</For></div>;
  if (component.kind === "consent") return <Consent component={component} checked={props.answers[component.factorId || component.id] === true} submitting={props.submitting} onSubmit={props.onSubmit} />;
  if (component.kind === "number") return <NumberInput component={component} value={props.answers[component.factorId || component.id]} submitting={props.submitting} onSubmit={props.onSubmit} />;
  if (component.kind === "field_group") return <FieldGroup component={component} answers={props.answers} submitting={props.submitting} onSubmit={props.onSubmit} />;
  return <LongText component={component} initialValue={String(props.answers[component.id] || "")} submitting={props.submitting} onSubmit={props.onSubmit} />;
}

function Consent(props: { component: Component; checked: boolean; submitting: boolean; onSubmit: (value: unknown) => void }) {
  const [checked, setChecked] = createSignal(props.checked);
  return <form class="fields" onSubmit={(event) => { event.preventDefault(); if (checked()) props.onSubmit(true); }}><label class="check"><input type="checkbox" required={props.component.required} checked={checked()} onInput={(event) => setChecked(event.currentTarget.checked)} /><span>{props.component.label}</span></label><button class="primary wide" disabled={props.submitting || !checked()}>Continue <Arrow /></button></form>;
}

function NumberInput(props: { component: Component; value: unknown; submitting: boolean; onSubmit: (value: unknown) => void }) {
  let input!: HTMLInputElement;
  return <form class="fields" onSubmit={(event) => { event.preventDefault(); if (input.reportValidity()) props.onSubmit(input.valueAsNumber); }}><label>{props.component.label}<input ref={input} type="number" required={props.component.required} min={props.component.minimum} max={props.component.maximum} value={typeof props.value === "number" ? props.value : ""} /></label><button class="primary wide" disabled={props.submitting}>Continue <Arrow /></button></form>;
}

function FieldGroup(props: { component: Component; answers: Record<string, unknown>; submitting: boolean; onSubmit: (value: unknown) => void }) {
  const [complete, setComplete] = createSignal(false);
  let form!: HTMLFormElement;
  function submitForm(event: SubmitEvent) {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    if (!form.reportValidity()) return;
    const data = new FormData(form);
    const value: Record<string, unknown> = {};
    for (const field of props.component.fields || []) value[field.id] = field.kind === "checkbox" ? data.has(field.id) : String(data.get(field.id) || "");
    props.onSubmit(value);
  }
  onMount(() => setComplete(form.checkValidity()));
  return <form ref={form} class="fields" onInput={() => setComplete(form.checkValidity())} onSubmit={submitForm}><For each={props.component.fields}>{(field) => <FieldView field={field} value={props.answers[field.id]} />}</For><button class="primary wide" classList={{ busy: props.submitting }} disabled={props.submitting || !complete()}>Continue <Arrow /></button></form>;
}

function FieldView(props: { field: Field; value: unknown }) {
  const field = props.field;
  if (field.kind === "checkbox") return <label class="check"><input name={field.id} type="checkbox" required={field.required} checked={props.value === true} /><span>{field.label}</span></label>;
  if (field.kind === "select") return <fieldset class="option-field"><legend>{field.label}</legend><div class="option-grid"><For each={field.options}>{(option) => <label class="option-choice"><input name={field.id} type="radio" value={option.value} required={field.required} checked={props.value === option.value} /><span><strong>{option.label}</strong><Show when={option.detail}><small>{option.detail}</small></Show></span></label>}</For></div></fieldset>;
  return <label>{field.label}<input name={field.id} type={field.kind} required={field.required} value={String(props.value || "")} maxlength={field.maxLength} autocomplete={field.kind === "email" ? "email" : field.id === "name" ? "name" : "organization"} /></label>;
}

function LongText(props: { component: Component; initialValue: string; submitting: boolean; onSubmit: (value: unknown) => void }) {
  let textarea!: HTMLTextAreaElement;
  return <div class="fields"><label>{props.component.label}<textarea ref={textarea} maxlength={props.component.maxLength} rows="7">{props.initialValue}</textarea></label><button class="primary wide" disabled={props.submitting} onClick={() => props.onSubmit(textarea.value)}>Continue <Arrow /></button></div>;
}

function ResultView(props: { result: NonNullable<SessionState["result"]> }) {
  const presentation = props.result.presentation;
  const actions = () => presentation.participantActions?.length ? presentation.participantActions : legacyActions(presentation.actionLabel, presentation.actionUrl);
  return <section class={`result-view ${props.result.outcome}`}><p class="eyebrow">Your next step</p><h1>{presentation.headline}</h1><p class="lede">{presentation.body}</p><div class="result-actions"><For each={actions()}>{(action, index) => <ResultAction action={action} primary={index() === 0} />}</For></div></section>;
}

function ResultAction(props: { action: ParticipantAction; primary: boolean }) {
  if (props.action.kind === "create_workspace") return <form method="post" action="/api/account-continuation" target="_top"><button class={props.primary ? "primary wide action" : "secondary-action action-button"} type="submit">{props.action.label}<Show when={props.primary}><Arrow /></Show></button></form>;
  if (inline && props.action.kind === "return") return null;
  if (!props.action.url) return <span class="secondary-action action-note">{props.action.label}</span>;
  return <a class={props.primary ? "primary wide action" : "secondary-action"} href={props.action.url} target={inline ? "_top" : undefined}>{props.action.label}<Show when={props.primary}><Arrow /></Show></a>;
}

function legacyActions(label?: string, url?: string): ParticipantAction[] {
  const actions: ParticipantAction[] = [];
  if (label && url) actions.push({ kind: url === "https://questionette.com/" ? "return" : "guided_service", label, url });
  actions.push({ kind: "create_workspace", label: "Create my workspace" });
  return actions;
}

function Loading(props: { problem?: string; label?: string; onRetry?: () => void }) {
  return <section class="journey loading"><div class="orbit" aria-hidden="true" /><p class="eyebrow">questionette</p><h1>{props.label || "Loading…"}</h1><Show when={props.problem}><p class="error">{props.problem}</p><button class="primary" onClick={props.onRetry}>Retry</button></Show></section>;
}

function Arrow() { return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M5 12h13M13 6l6 6-6 6" /></svg>; }
function BackArrow() { return <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M19 12H6M11 7l-5 5 5 5" /></svg>; }
